// Join.js - TRAKtag Group Quiz
// Version: 2.2
// Features: 6-char code, quiz preview, countdown, 250+ avatars, DiceBear, client logo, ad space

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

// 250+ Categorised Avatars
const AVATAR_CATEGORIES = {
  faces: {
    icon: '😀',
    label: 'Faces',
    emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😉','😊','😇','🥰','😍','🤩','😘','😎','🤓','🧐','🤨','😏','😌','😴','🤤','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😷','🤒','🤕','🤢','🤮','🥵','🥶','🥴','😵','🤯','🤠','🥳','😎','🤓','🧐']
  },
  animals: {
    icon: '🦊',
    label: 'Animals',
    emojis: ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜','🦟','🦗','🕷','🦂','🐢','🐍','🦎','🦖','🦕','🐙','🦑','🦐','🦞','🦀','🐡','🐠','🐟','🐬','🐳','🐋','🦈','🐊','🐅','🐆','🦓','🦍','🦧','🐘']
  },
  festive: {
    icon: '🎄',
    label: 'Festive',
    emojis: ['🎅','🤶','🧑‍🎄','🧝','⛄','❄️','🎄','🎁','🦌','🔔','🕯️','✨','🎆','🎇','🧨','🎀','🎊','🎉','🪅','🎃','👻','💀','☠️','👽','🤖','🎭','🪄','🔮','🧿','💝','💖','💗','💓','💕']
  },
  fantasy: {
    icon: '🦄',
    label: 'Fantasy',
    emojis: ['🦄','🐲','🧙','🧚','🧛','🧜','🧝','🧞','🧟','🦸','🦹','👽','🤖','👻','💀','🎃','👹','👺','🤡','💩','😺','😸','😹','😻','😼','😽','🙀','😿','😾','🐉','🦴','🌟','⭐','🌙','☀️','🌈','🔥','💫','✨']
  },
  food: {
    icon: '🍕',
    label: 'Food',
    emojis: ['🍕','🍔','🍟','🌭','🍿','🥓','🥚','🧇','🥞','🍳','🥗','🥪','🌮','🌯','🥙','🍖','🍗','🥩','🍤','🥟','🍜','🍝','🍣','🍱','🍛','🍚','🍙','🍘','🍡','🍧','🍨','🍦','🥧','🧁','🍰','🎂','🍮','🍭','🍬','🍫','🍿','🍩','🍪','🥛','🍼','☕','🍵','🧃','🥤','🍶','🍺','🍻','🥂','🍷']
  },
  sports: {
    icon: '⚽',
    label: 'Sports',
    emojis: ['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🏓','🏸','🏒','🥅','⛳','🏹','🎣','🥊','🥋','🎽','🛹','🛼','🛷','⛸','🥌','🎿','⛷','🏂','🪂','🏋️','🤼','🤸','⛹️','🤺','🤾','🏌️','🏇','🧘','🏄','🏊','🤽','🚣','🧗','🚴','🚵']
  },
  objects: {
    icon: '🎮',
    label: 'Objects',
    emojis: ['🎮','🕹️','🎲','🎯','🎨','🎭','🎪','🎠','🎡','🎢','🚀','✈️','🚁','🛸','🚗','🏎️','🏍️','🚲','🛵','🚂','⛵','🚢','⚓','🪁','🎈','🎁','🎀','🏆','🥇','🥈','🥉','🏅','🎖️','📱','💻','🖥️','🎧','🎤','🎸','🎹','🎺','🎷','🥁','🎻','📷','🔭','🔬','💎','👑','🎩']
  }
};

// DiceBear styles
const DICEBEAR_STYLES = [
  { id: 'adventurer', label: 'Adventurer' },
  { id: 'avataaars', label: 'Classic' },
  { id: 'bottts', label: 'Robot' },
  { id: 'pixel-art', label: 'Pixel' },
  { id: 'lorelei', label: 'Artistic' },
  { id: 'thumbs', label: 'Thumbs' }
];

function Join() {
  const { code: urlCode } = useParams();
  const navigate = useNavigate();
  
  const [joinCode, setJoinCode] = useState(urlCode || '');
  const [playerName, setPlayerName] = useState('');
  const [avatar, setAvatar] = useState('😀');
  const [avatarType, setAvatarType] = useState('emoji'); // 'emoji' or 'dicebear'
  const [step, setStep] = useState(urlCode ? 2 : 1);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState('');
  
  // Avatar picker state
  const [activeCategory, setActiveCategory] = useState('faces');
  const [dicebearStyle, setDicebearStyle] = useState('adventurer');
  const [dicebearSeed, setDicebearSeed] = useState(Math.random().toString(36).substring(7));

  useEffect(() => {
    if (urlCode) {
      checkSession(urlCode);
    }
  }, [urlCode]);

  // Countdown timer
  useEffect(() => {
    if (!session?.scheduledStartAt) {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const start = new Date(session.scheduledStartAt);
      const diff = start - now;

      if (diff <= 0) {
        setCountdown('Starting soon...');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setCountdown(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [session]);

  const checkSession = async (code) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.joinSession(code, 'temp', '😀');
      setSession(response.session);
      setStep(2);
    } catch (err) {
      setError('Please check your code. The code entered is not a listed Quiz.');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (joinCode.length === 6) {
      checkSession(joinCode);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    // Determine avatar value
    const avatarValue = avatarType === 'dicebear' 
      ? `dicebear:${dicebearStyle}:${dicebearSeed}`
      : avatar;

    try {
      const response = await api.joinSession(joinCode, playerName, avatarValue);
      localStorage.setItem('sessionToken', response.sessionToken);
      localStorage.setItem('participantId', response.participant.id);
      localStorage.setItem('playerName', response.participant.playerName);
      localStorage.setItem('playerAvatar', response.participant.avatarEmoji);
      navigate(`/play/${response.session.id}`);
    } catch (err) {
      setError(err.message || 'Failed to join');
    } finally {
      setLoading(false);
    }
  };

  const selectRandomEmoji = () => {
    const emojis = AVATAR_CATEGORIES[activeCategory].emojis;
    const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
    setAvatar(randomEmoji);
    setAvatarType('emoji');
  };

  const regenerateDicebear = () => {
    setDicebearSeed(Math.random().toString(36).substring(7));
  };

  const selectDicebear = () => {
    setAvatarType('dicebear');
  };

  const getDicebearUrl = () => {
    return `https://api.dicebear.com/7.x/${dicebearStyle}/svg?seed=${dicebearSeed}`;
  };

  return (
    <div className="join-page">
      {/* Client/TRAKtag Logo */}
      <div className="join-logo">
        {session?.clientLogoUrl ? (
          <img src={session.clientLogoUrl} alt="Quiz Logo" className="client-logo" />
        ) : (
          <div className="traktag-logo">🎯 TRAKtag</div>
        )}
      </div>

      <div className="join-card">
        <h1>Join Quiz</h1>
        
        {step === 1 && (
          <>
            <p className="subtitle">Enter the quiz code from your host</p>
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleCodeSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="ABC123"
                  className="code-input"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
              
              <button type="submit" className="btn btn-primary" disabled={loading || joinCode.length !== 6}>
                {loading ? 'Checking...' : '🎮 Find Quiz'}
              </button>
            </form>

            {/* App Promo */}
            <div className="app-promo">
              <h3>📱 Better in the App!</h3>
              <p>Get push notifications, haptic feedback, and smoother gameplay</p>
              <a href="/download" className="app-link">Download TRAKtag App →</a>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Quiz Info Preview */}
            <div className="quiz-preview">
              <div className="quiz-preview-icon">🎪</div>
              <h2 className="quiz-preview-title">{session?.title || 'Quiz'}</h2>
              {session?.hostName && (
                <p className="quiz-preview-host">Host: {session.hostName}</p>
              )}
              {countdown ? (
                <div className="quiz-preview-countdown">
                  <span className="countdown-label">Starts in:</span>
                  <span className="countdown-time">{countdown}</span>
                </div>
              ) : (
                <p className="quiz-preview-waiting">Waiting for host to start</p>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleJoin}>
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter your name"
                  maxLength={30}
                  required
                  autoFocus
                />
              </div>

              {/* Avatar Picker */}
              <div className="form-group">
                <div className="avatar-header">
                  <label>Choose Avatar</label>
                  <button type="button" className="random-btn" onClick={selectRandomEmoji}>
                    🎲 Random
                  </button>
                </div>

                {/* Category Tabs */}
                <div className="avatar-tabs">
                  {Object.entries(AVATAR_CATEGORIES).map(([key, cat]) => (
                    <button
                      key={key}
                      type="button"
                      className={`avatar-tab ${activeCategory === key ? 'active' : ''}`}
                      onClick={() => setActiveCategory(key)}
                    >
                      {cat.icon}
                    </button>
                  ))}
                </div>

                {/* Emoji Grid */}
                <div className="avatar-grid">
                  {AVATAR_CATEGORIES[activeCategory].emojis.map((emoji, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`avatar-option ${avatarType === 'emoji' && avatar === emoji ? 'selected' : ''}`}
                      onClick={() => { setAvatar(emoji); setAvatarType('emoji'); }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                {/* DiceBear Section */}
                <div className="dicebear-section">
                  <div className="dicebear-header">
                    <span>✨ Or generate unique avatar</span>
                    <button type="button" className="refresh-btn" onClick={regenerateDicebear}>
                      🔄 New
                    </button>
                  </div>
                  
                  <div className="dicebear-preview-row">
                    <div 
                      className={`dicebear-preview ${avatarType === 'dicebear' ? 'selected' : ''}`}
                      onClick={selectDicebear}
                    >
                      <img src={getDicebearUrl()} alt="Custom Avatar" />
                    </div>
                    
                    <div className="dicebear-styles">
                      {DICEBEAR_STYLES.map(style => (
                        <button
                          key={style.id}
                          type="button"
                          className={`style-btn ${dicebearStyle === style.id ? 'active' : ''}`}
                          onClick={() => setDicebearStyle(style.id)}
                        >
                          {style.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Selected Avatar Display */}
                <div className="selected-avatar">
                  <span>Selected: </span>
                  {avatarType === 'emoji' ? (
                    <span className="selected-emoji">{avatar}</span>
                  ) : (
                    <img src={getDicebearUrl()} alt="Selected" className="selected-dicebear" />
                  )}
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Joining...' : '🎮 Join Quiz Now!'}
              </button>
              
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setStep(1); setError(''); setSession(null); }}
              >
                ← Back
              </button>
            </form>
          </>
        )}

        {/* Ad Space */}
        <div className="ad-space">
          <div className="ad-placeholder">
            📺 Ad Space - 320x50
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="join-footer">
        Powered by <strong>TRAKtag</strong>
      </div>
    </div>
  );
}

export default Join;
